import { useNavigate } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import { Button } from '../components/ui/button'
import MarketingHeader from '../components/MarketingHeader'
import AppFooter from '../components/AppFooter'
import { Magnetic, Reveal } from '../components/celebration'

const About = () => {
  const { language } = useLanguage()
  const navigate = useNavigate()
  const isKa = language === 'ka'

  // The mission, as four named promises under one lead sentence.
  const mission = [
    {
      title: isKa ? 'დაზოგილი დრო და ენერგია' : 'Time and energy saved',
      body: isKa
        ? 'გავათავისუფლოთ მასპინძლები ქაღალდის მოსაწვევების დარიგებისა და სტუმრების სათითაოდ ჩანიშვნისგან.'
        : 'Free hosts from handing out paper invitations and ticking guests off one by one.',
    },
    {
      title: isKa ? 'სურვილების სია' : 'A wishlist',
      body: isKa
        ? 'დავეხმაროთ სტუმრებს სასურველი და საჭირო საჩუქრების შერჩევაში, ხოლო მასპინძლებს — განმეორებითი ან არასაჭირო საჩუქრების თავიდან აცილებაში.'
        : 'Help guests pick gifts that are wanted and needed, and spare hosts the duplicates and the things nobody needed.',
    },
    {
      title: isKa ? 'ინდივიდუალური სტილის ხაზგასმა' : 'Room for personal style',
      body: isKa
        ? 'მივცეთ თითოეულ მომხმარებელს შესაძლებლობა, შექმნას თავისი ღონისძიების ხასიათზე მორგებული, უნიკალური და ესთეტიკური ორენოვანი ციფრული სივრცე, შეინახოს დიზაინები და სასურველი გახადოს საჯარო.'
        : 'Give everyone a unique, beautiful bilingual space shaped around their own event — keep the designs, and make a favourite one public.',
    },
    {
      title: isKa ? 'ორგანიზების ახალი სტანდარტი' : 'A new standard for organising',
      body: isKa
        ? 'დავნერგოთ საქართველოში ღონისძიებების დაგეგმვისა და მართვის თანამედროვე, მოსახერხებელი და ინოვაციური კულტურა.'
        : 'Bring a modern, convenient and innovative culture of event planning and management to Georgia.',
    },
  ]

  return (
    <div className="bg-story-gradient min-h-[100dvh] text-[#1b1033] dark:text-[#f4f0ff]">
      <MarketingHeader />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#1b1033]/10 dark:border-white/10">
        <div
          aria-hidden="true"
          className="blob blob-pink blob-drift"
          style={{ width: 420, height: 420, top: -140, left: -150, opacity: 0.4 }}
        />
        <div
          aria-hidden="true"
          className="blob blob-violet blob-drift-slow"
          style={{ width: 460, height: 460, top: 60, right: -190, opacity: 0.35 }}
        />
        <div className="relative mx-auto max-w-3xl px-5 py-20 text-center lg:py-28">
          <Reveal>
            <span className="eyebrow">
              <span className="eyebrow-dot" />
              {isKa ? 'ჩვენ შესახებ' : 'About us'}
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display font-bold tracking-tight text-balance text-[clamp(2.4rem,5.5vw,4rem)] leading-[1.04]">
              {isKa ? 'ვაქცევთ მოწვევას ' : 'We turn invitations into '}
              <span className="text-gradient-brand">{isKa ? 'ამბად' : 'stories'}</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-5 max-w-[52ch] text-lg leading-relaxed text-[#4a3d66] dark:text-[#c3b8dd]">
              {isKa
                ? 'Storyfy არის ციფრული მოსაწვევების პლატფორმა, რომელიც ერთ ადგილას აერთიანებს დიზაინს, RSVP-ს, საჩუქრებსა და სტუმრების მართვას.'
                : 'Storyfy is a digital invitation platform that brings design, RSVPs, gifts and guest management together in one place.'}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Mission */}
      <section className="relative overflow-hidden border-b border-[#1b1033]/10 dark:border-white/10">
        <div className="relative mx-auto grid max-w-6xl gap-12 px-5 py-16 lg:grid-cols-2 lg:gap-16 lg:py-24">
          <Reveal>
            <h2 className="font-display text-[clamp(1.8rem,3.2vw,2.5rem)] font-bold tracking-tight">
              {isKa ? 'ჩვენი მისია' : 'Our mission'}
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="text-lg leading-relaxed text-[#4a3d66] dark:text-[#c3b8dd]">
              <p>
                {isKa
                  ? 'სრულყოფილი ციფრული გამოცდილება — ერთიანი პლატფორმა, სადაც ღონისძიების მოსაწვევებს, სტუმრების და საჩუქრების სიას რამდენიმე წუთში, მარტივად შექმნი და მართავ.'
                  : 'A complete digital experience — one platform where you create and manage your event’s invitations, guest list and gift list in minutes.'}
              </p>
              <ul className="mt-6 space-y-4">
                {mission.map(({ title, body }) => (
                  <li key={title} className="border-l-2 border-celebration-pink/40 pl-4">
                    <strong className="font-display font-bold text-[#1b1033] dark:text-[#f4f0ff]">
                      {title}
                    </strong>{' '}
                    — {body}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-grad-juicy">
        <div
          aria-hidden="true"
          className="blob"
          style={{ width: 360, height: 360, top: -150, left: '8%', background: '#fff', opacity: 0.2 }}
        />
        <div
          aria-hidden="true"
          className="blob"
          style={{ width: 320, height: 320, bottom: -150, right: '8%', background: '#fff', opacity: 0.18 }}
        />
        <div className="relative mx-auto max-w-6xl px-5 py-16 text-center lg:py-20">
          <Reveal>
            <h2 className="mx-auto max-w-[22ch] font-display text-balance text-[clamp(1.9rem,4vw,2.75rem)] font-bold leading-[1.07] tracking-tight text-white">
              {isKa ? 'შემოგვიერთდი და შექმენი პირველი მოსაწვევი' : 'Join us and create your first invitation'}
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <Magnetic className="mt-8 inline-block">
              <Button
                onClick={() => navigate('/login')}
                className="group h-12 rounded-full bg-white px-7 text-[15px] font-semibold text-[#1b1033] shadow-celebrate-sm transition-all duration-300 ease-spring hover:bg-white/90 hover:shadow-celebrate-md active:scale-95"
                data-testid="about-cta"
              >
                {isKa ? 'დაიწყე' : 'Get started'}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </Magnetic>
          </Reveal>
        </div>
      </section>

      <AppFooter maxWidth="max-w-6xl" />
    </div>
  )
}

export default About
